/**
 * One function per endpoint, named after the pipeline stage it serves. The response shape lives
 * next to the fetch so a server contract change breaks here and nowhere else.
 */

import { apiGet, apiPost, fetchAudio } from 'src/api/client';
import type { PipelineInstanceState } from 'src/lib/jobBoard';
import type {
  PipelineJob,
  PipelineJobStatus,
  PipelineStage,
  EditorialChannel,
  EditorialChannelWithStats,
  EditorialCriteriaMatch,
  EditorialEnrichment,
  EditorialFeedItem,
  EditorialFeedSource,
  EditorialFeedSourceWithStats,
  FeedPreview,
  EditorialOutput,
  EditorialPrenota,
  EditorialReview,
  EditorialSignal,
  EditorialTopic,
  EditorialTopicMention,
  EditorialTopicTrend,
  EditorialTopicWithTrend,
  EditorialSpeakerClip,
  EditorialSpeakerCommand,
  EditorialSpeakerCommandType,
  EditorialSpeakerProfile,
  EditorialStoryCandidate,
  EditorialStreamSummary,
  EditorialStream,
  EditorialTopics,
  EditorialTranscript,
  EditorialVerification,
  EditorialXReview,
  EditorialRelevanceWatch,
  EditorialAlertEvent,
  EditorialLlmCosts,
  EditorialAlertRule,
  EditorialAlertSurface,
  Person,
  SuggestedName,
  UserRole,
  UserStatus,
} from 'src/types/editorial';

// ── channels ─────────────────────────────────────────────────────────────────

export function fetchChannels(): Promise<{ channels: EditorialChannelWithStats[] }> {
  return apiGet('/channel/filter-channels');
}

export function createChannel(input: { channelUrl: string; name?: string; notes?: string }): Promise<{ channel: EditorialChannel }> {
  return apiPost('/channel/create-channel', input);
}

export function updateChannel(input: { channelId: string; active?: boolean; name?: string; notes?: string }): Promise<{ channel: EditorialChannel }> {
  const { channelId, ...body } = input;
  return apiPost(`/channel/update-channel/${encodeURIComponent(channelId)}`, body);
}

export function deleteChannel(channelId: string): Promise<{ channelId: string }> {
  return apiPost(`/channel/delete-channel/${encodeURIComponent(channelId)}`, {});
}

// ── feed sources and their posts ─────────────────────────────────────────────

export function fetchFeedSources(): Promise<{ sources: EditorialFeedSourceWithStats[] }> {
  return apiGet('/feed/filter-sources');
}

/** What this address would give, before anything is written. Safe to call on every attempt. */
export function previewFeedSource(url: string): Promise<{ preview: FeedPreview }> {
  return apiPost('/feed/preview-source', { url });
}

/** The address may be the feed or the publisher's homepage — the server works out which, and
 *  reads it once before answering, so `newItems` is what the subscription brought in already. */
export function createFeedSource(input: { url: string; name?: string; notes?: string }): Promise<{ source: EditorialFeedSource; newItems: number }> {
  return apiPost('/feed/create-source', input);
}

export function updateFeedSource(input: { sourceId: string; active?: boolean; name?: string; notes?: string }): Promise<{ source: EditorialFeedSource }> {
  const { sourceId, ...body } = input;
  return apiPost(`/feed/update-source/${encodeURIComponent(sourceId)}`, body);
}

export function deleteFeedSource(sourceId: string): Promise<{ sourceId: string }> {
  return apiPost(`/feed/delete-source/${encodeURIComponent(sourceId)}`, {});
}

/** Read it now instead of at the next quarter hour. */
export function pollFeedSource(sourceId: string): Promise<{ source: EditorialFeedSource; newItems: number; error: string }> {
  return apiPost(`/feed/poll-source/${encodeURIComponent(sourceId)}`, {});
}

export function fetchFeedItems(params: { page?: number; rowsPerPage?: number; sourceId?: string; search?: string } = {}): Promise<{
  items: EditorialFeedItem[];
  totalRecords: number;
  hasNextPage: boolean;
  totalPages: number;
}> {
  const query = new URLSearchParams();
  query.set('page', String(params.page ?? 1));
  query.set('rowsPerPage', String(params.rowsPerPage ?? 25));
  if (params.sourceId) query.set('sourceId', params.sourceId);
  if (params.search) query.set('search', params.search);

  return apiGet(`/feed/filter-items?${query.toString()}`);
}

export function fetchStreams(page = 1, rowsPerPage = 50): Promise<{ streams: EditorialStream[]; total: number }> {
  return apiGet(`/stream/filter-streams?page=${page}&rowsPerPage=${rowsPerPage}`);
}

/** `get-stream` also returns prenotas and signals; the workspaces fetch those separately so each
 *  one owns its own loading state. Only the stream is read from here. */
export function fetchStream(streamId: string): Promise<{ stream: EditorialStream }> {
  return apiGet(`/stream/get-stream/${encodeURIComponent(streamId)}`);
}

export function fetchTranscript(streamId: string): Promise<{ transcript: EditorialTranscript | null }> {
  return apiGet(`/stream/get-transcript/${encodeURIComponent(streamId)}`);
}

export function fetchTopics(streamId: string): Promise<{ topics: EditorialTopics | null }> {
  return apiGet(`/stream/get-topics/${encodeURIComponent(streamId)}`);
}

export function fetchSignals(streamId: string): Promise<{ signals: EditorialSignal[]; total: number }> {
  return apiGet(`/stream/get-signals/${encodeURIComponent(streamId)}`);
}

export function fetchCriteriaMatches(
  streamId: string,
  { matchedOnly = true }: { matchedOnly?: boolean } = {}
): Promise<{ criteriaMatches: EditorialCriteriaMatch[]; total: number }> {
  return apiGet(`/stream/get-criteria-matches/${encodeURIComponent(streamId)}?matchedOnly=${matchedOnly}`);
}

export function fetchStoryCandidates(
  streamId: string
): Promise<{ storyCandidates: EditorialStoryCandidate[]; total: number }> {
  return apiGet(`/stream/get-story-candidates/${encodeURIComponent(streamId)}`);
}

export function fetchPrenotas(streamId: string): Promise<{ prenotas: EditorialPrenota[]; total: number }> {
  return apiGet(`/stream/get-prenotas/${encodeURIComponent(streamId)}`);
}

export function fetchXReviews(streamId: string): Promise<{ xReviews: EditorialXReview[]; total: number }> {
  return apiGet(`/stream/get-x-reviews/${encodeURIComponent(streamId)}`);
}

/** The X review is one measurement; this is what the score did over the five hours after it. */
export function fetchRelevanceWatches(streamId: string): Promise<{ watches: EditorialRelevanceWatch[]; total: number }> {
  return apiGet(`/stream/get-relevance-watches/${encodeURIComponent(streamId)}`);
}

export function fetchVerifications(
  streamId: string
): Promise<{ verifications: EditorialVerification[]; total: number }> {
  return apiGet(`/stream/get-verifications/${encodeURIComponent(streamId)}`);
}

export function fetchEnrichments(streamId: string): Promise<{ enrichments: EditorialEnrichment[]; total: number }> {
  return apiGet(`/stream/get-enrichments/${encodeURIComponent(streamId)}`);
}

export function fetchOutputs(streamId: string): Promise<{ outputs: EditorialOutput[]; total: number }> {
  return apiGet(`/stream/get-outputs/${encodeURIComponent(streamId)}`);
}

export function fetchReviews(streamId: string): Promise<{ reviews: EditorialReview[]; total: number }> {
  return apiGet(`/stream/get-reviews/${encodeURIComponent(streamId)}`);
}

// ── feedback ─────────────────────────────────────────────────────────────────

export type FeedbackVerdict = 'approved' | 'rejected' | 'needs_review';

/** `reason` is mandatory server-side — feedback without a reason is not measurable. */
export function submitFeedback(input: {
  targetType: 'prenota' | 'signal' | 'stream' | 'topic_mention';
  targetId: string;
  verdict: FeedbackVerdict;
  reason: string;
}): Promise<{ saved: boolean }> {
  return apiPost('/stream/submit-feedback', input);
}

export type ReviewAction = 'approved' | 'rejected';
export type ReviewReasonCategory = 'factual' | 'attribution' | 'style' | 'scope' | 'format' | 'other';

/**
 * One editor verdict on one draft. Append-only on the server — the pipeline's learning pass reads
 * these records for style exemplars and the relevance floor, so a verdict left here changes what
 * the next broadcast drafts.
 */
export function submitReview(input: {
  transformationId: string;
  action: ReviewAction;
  reasonCategory: ReviewReasonCategory;
  comment?: string;
}): Promise<{ review: EditorialReview }> {
  return apiPost('/stream/submit-review', input);
}

// ── voice profiles ───────────────────────────────────────────────────────────

export function fetchSpeakerProfiles(
  includeArchived = false
): Promise<{ profiles: EditorialSpeakerProfile[]; commands: EditorialSpeakerCommand[]; total: number }> {
  return apiGet(`/speaker-profile/filter-profiles?includeArchived=${includeArchived}`);
}

export function fetchSpeakerProfile(
  profileId: string,
  clipLimit = 60
): Promise<{ profile: EditorialSpeakerProfile; clips: EditorialSpeakerClip[]; commands: EditorialSpeakerCommand[]; suggestedNames: SuggestedName[] }> {
  return apiGet(`/speaker-profile/get-profile/${encodeURIComponent(profileId)}?clipLimit=${clipLimit}`);
}

/**
 * The pipeline box owns the training audio and the voice encoder, so nothing here changes a
 * profile directly — every edit is queued and applied on the pipeline's next run.
 */
export function submitSpeakerCommand(input: {
  profileId: string;
  type: EditorialSpeakerCommandType;
  payload?: Record<string, unknown>;
}): Promise<{ commandId: string }> {
  const { profileId, ...body } = input;
  return apiPost(`/speaker-profile/submit-command/${encodeURIComponent(profileId)}`, body);
}

/**
 * Declares a voice the pipeline has never heard — a new spokesperson, someone about to start
 * appearing. The id comes back immediately so clips can be uploaded for it in the same sitting,
 * before the pipeline has created the record.
 */
export function createSpeakerProfile(input: { name: string; notes?: string }): Promise<{ profileId: string; commandId: string }> {
  return apiPost('/speaker-profile/create-profile', input);
}

/**
 * Uploads one voice sample. Base64 in the body rather than multipart: a sample is seconds of
 * speech, and the encoder that will actually read it lives on the pipeline box either way.
 */
export function uploadSpeakerClip(input: {
  profileId: string;
  audioBase64: string;
  format: string;
  label?: string;
}): Promise<{ commandId: string }> {
  const { profileId, ...body } = input;
  return apiPost(`/speaker-profile/upload-clip/${encodeURIComponent(profileId)}`, body);
}

/**
 * Clip audio is served by the API, not by a bucket URL — these are voice recordings of
 * identifiable people, and a link anyone can replay is the wrong access model.
 *
 * Fetched as bytes and handed to the player as an object URL, because the endpoint now requires a
 * token and an `<audio src>` sends no Authorization header. The caller owns the returned URL and
 * must `URL.revokeObjectURL` it.
 */
export function fetchClipAudio(clipId: string): Promise<string> {
  return fetchAudio(`/speaker-profile/get-clip-audio/${encodeURIComponent(clipId)}`);
}

/** One request for every count the orientation screens draw. */
export function fetchStreamSummary(streamId: string): Promise<{ summary: EditorialStreamSummary }> {
  return apiGet(`/stream/get-stream-summary/${encodeURIComponent(streamId)}`);
}

// ── processing queue ─────────────────────────────────────────────────────────

export function fetchPipelineJobs(input: { status?: PipelineJobStatus; streamId?: string; limit?: number } = {}): Promise<{ jobs: PipelineJob[]; machine?: PipelineInstanceState }> {
  const query = new URLSearchParams();
  if (input.status) query.set('status', input.status);
  if (input.streamId) query.set('streamId', input.streamId);
  if (input.limit) query.set('limit', String(input.limit));

  return apiGet(`/job/filter-jobs${query.size ? `?${query}` : ''}`);
}

export function fetchPipelineJob(jobId: string): Promise<{ job: PipelineJob }> {
  return apiGet(`/job/get-job/${encodeURIComponent(jobId)}`);
}

/**
 * Queues a video, or the next stages of one already transcribed. `sourceUrl` and `streamId` are
 * alternatives: a URL is a video nobody has processed, a streamId is one whose transcript exists.
 */
export function submitPipelineJob(input: {
  sourceUrl?: string;
  streamId?: string;
  stages: PipelineStage[];
}): Promise<{ jobId: string; instanceStarted: boolean }> {
  return apiPost('/job/submit-job', input);
}

// ── people ───────────────────────────────────────────────────────────────────

export function fetchPeople(): Promise<{ users: Person[]; total: number }> {
  return apiGet('/user/filter-users');
}

/** The password comes back once, in this response. It is never stored in readable form. */
export function createPerson(input: { name: string; email: string; role: UserRole }): Promise<{ user: Person; password: string }> {
  return apiPost('/user/create-user', input);
}

export function resetPersonPassword(userId: string): Promise<{ password: string }> {
  return apiPost('/user/reset-password', { userId });
}

export function setPersonStatus(input: { userId: string; status: UserStatus }): Promise<{ user: Person }> {
  return apiPost('/user/set-status', input);
}

/** Who the server currently thinks the caller is — role and status included, both of which can
 *  have changed since the token was issued. */
export function fetchSession(): Promise<{ user: Person }> {
  return apiGet('/auth/get-session');
}


// ── canonical topics ─────────────────────────────────────────────────────────

export function filterTopics(input: {
  category?: string;
  search?: string;
  minMentions?: number;
  /** 1-based, like every other paginated endpoint here. */
  page?: number;
  rowsPerPage?: number;
}): Promise<{ topics: EditorialTopicWithTrend[]; totalRecords: number; totalPages: number; hasNextPage: boolean }> {
  return apiPost('/topic/filter-topics', input);
}

export function fetchCanonicalTopic(topicId: string): Promise<{
  topic: EditorialTopic;
  trend: EditorialTopicTrend;
  mentions: EditorialTopicMention[];
  /** The intra-day curves. The trend's own timeline buckets by day; this is below it. */
  relevanceWatches: EditorialRelevanceWatch[];
}> {
  return apiGet(`/topic/get-topic/${encodeURIComponent(topicId)}`);
}

// ── alerts ───────────────────────────────────────────────────────────────────

export function fetchAlertRules(includeInactive = true): Promise<{ rules: EditorialAlertRule[]; total: number }> {
  return apiGet(`/alert/filter-rules?includeInactive=${includeInactive}`);
}

export type AlertRuleInput = {
  name: string;
  keywords: string[];
  matchAll?: boolean;
  surfaces?: EditorialAlertSurface[];
  channelIds?: string[];
  minRelevanceScore?: number | null;
  notifyEmails?: string[];
  cooldownMinutes?: number;
  active?: boolean;
};

/** A rule is armed the moment it is saved — the pipeline reads the collection fresh per chunk. */
export function createAlertRule(input: AlertRuleInput): Promise<{ rule: EditorialAlertRule }> {
  return apiPost('/alert/create-rule', input);
}

/** Only the fields sent are changed, so the pause toggle can send `active` alone. */
export function updateAlertRule(input: { ruleId: string } & Partial<AlertRuleInput>): Promise<{ rule: EditorialAlertRule }> {
  const { ruleId, ...body } = input;
  return apiPost(`/alert/update-rule/${encodeURIComponent(ruleId)}`, body);
}

/** The rule stops firing; the alerts it already raised are kept. */
export function deleteAlertRule(ruleId: string): Promise<{ ruleId: string }> {
  return apiPost(`/alert/delete-rule/${encodeURIComponent(ruleId)}`, {});
}

export function filterAlertEvents(input: {
  streamId?: string;
  ruleId?: string;
  surface?: EditorialAlertSurface;
  /** ISO instant. Only events raised after it — what the unread count asks about. */
  since?: string;
  page?: number;
  /** 0 asks for the count alone and transfers no events. */
  rowsPerPage?: number;
}): Promise<{ events: EditorialAlertEvent[]; totalRecords: number; totalPages: number; hasNextPage: boolean }> {
  return apiPost('/alert/filter-events', input);
}

/** How many alerts have been raised since an instant, without downloading any of them. */
export function countAlertEventsSince(since: string): Promise<{ totalRecords: number }> {
  return apiPost('/alert/filter-events', { since, rowsPerPage: 0 });
}

/**
 * What the pipeline has spent on models over a window of days.
 *
 * The box writes one row per call into Mongo precisely so this reading survives it being stopped,
 * which it is for most of the day.
 */
export function fetchLlmCosts(days: number): Promise<EditorialLlmCosts> {
  return apiGet(`/usage/get-llm-costs?days=${days}`);
}
